import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { chatsApi } from '../../api/chats'
import { usersApi } from '../../api/users'
import { useChatStore } from '../../stores/chatStore'
import { useAuthStore } from '../../stores/authStore'
import { ChatItem } from './ChatItem'
import { Spinner } from '../ui/Spinner'
import { useUiStore } from '../../stores/uiStore'
import { messageCache } from '../../lib/messageCache'
import { NewChatModal } from '../sidebar/NewChatModal'
import { CreateGroupModal } from '../sidebar/CreateGroupModal'
import { SettingsPanel } from '../sidebar/SettingsPanel'
import { ContactsPanel } from '../sidebar/ContactsPanel'
import { CreateChannelModal } from '../channels/CreateChannelModal'
import { ChannelView } from '../channels/ChannelView'
import { channelsApi, type Channel } from '../../api/channels'
import { groupsApi } from '../../api/groups'
import { StoryFeed } from '../stories/StoryFeed'
import { BotStore } from '../sidebar/BotStore'
import { useFoldersStore } from '../../stores/foldersStore'

type Modal = 'new-chat' | 'create-group' | 'create-channel' | 'bot-store' | 'settings' | 'contacts' | null

import type { Chat } from '../../types'
import { chatTitle } from '../../lib/displayName'

function SavedMessagesButton({ activeChatId, chats, userId, addToast }: {
  activeChatId: string | null
  chats: Chat[]
  userId: string | null
  addToast: (type: 'error' | 'success' | 'info', msg: string) => void
}) {
  const isActive = activeChatId != null &&
    chats.some((c) => c.id === activeChatId && c.type === 'private' && !c.peer)

  return (
    <button
      onClick={async () => {
        if (!userId) return
        try {
          const chat = await chatsApi.getOrCreate(userId)
          useChatStore.getState().upsertChat({ ...chat, title: 'Избранное' })
          useChatStore.getState().setActiveChat(chat.id)
        } catch {
          addToast('error', 'Не удалось открыть Избранное')
        }
      }}
      className={`flex items-center gap-3 px-4 py-2 transition-colors w-full text-left ${isActive ? 'bg-bg-active' : 'hover:bg-bg-hover'}`}
    >
      <div className="w-[52px] h-[52px] rounded-full bg-gradient-to-br from-blue-400 to-accent flex items-center justify-center shrink-0">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-txt-primary text-[15px] font-semibold">Избранное</p>
        <p className="text-txt-secondary text-[13px] truncate">Сохранённые сообщения</p>
      </div>
    </button>
  )
}

export function ChatList() {
  const { chats, setChats, activeChatId, setActiveChat, unread } = useChatStore()
  const { addToast, setGlobalSearchOpen } = useUiStore()

  // Per-folder unread: sum unread counts for chats belonging to each folder
  const folderUnread = useMemo(() => {
    const map: Record<string, number> = {}
    for (const chat of chats) {
      const f = chat.folder ?? 'inbox'
      const count = unread[chat.id] ?? chat.unread_count ?? 0
      map[f] = (map[f] ?? 0) + count
    }
    return map
  }, [chats, unread])
  const { userId } = useAuthStore()
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [search, setSearch] = useState('')
  const [modal, setModal] = useState<Modal>(null)
  // Завязываем панели (настройки/новый чат/...) на history, чтобы аппаратная/
  // браузерная кнопка «назад» закрывала панель, а не выходила с сайта.
  const modalPushedRef = useRef(false)
  useEffect(() => {
    if (modal && !modalPushedRef.current) {
      modalPushedRef.current = true
      window.history.pushState({ ggramPanel: true }, '')
    } else if (!modal && modalPushedRef.current) {
      modalPushedRef.current = false
      if ((window.history.state as { ggramPanel?: boolean } | null)?.ggramPanel) {
        window.history.back()
      }
    }
  }, [modal])
  useEffect(() => {
    const onPop = () => {
      if (modalPushedRef.current) {
        modalPushedRef.current = false
        setModal(null)
      }
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])
  const [folder, setFolder] = useState('inbox')
  const { folders, addFolder, removeFolder, updateFolder } = useFoldersStore()
  const [newFolderName, setNewFolderName] = useState('')
  const [showNewFolder, setShowNewFolder] = useState(false)
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null)
  const [editFolderName, setEditFolderName] = useState('')
  const [channels, setChannels] = useState<Channel[]>([])
  const [channelsExpanded, setChannelsExpanded] = useState(false)
  const [activeChannelId, setActiveChannelId] = useState<string | null>(null)

  const enrichChats = useCallback(async (rawChats: import('../../types').Chat[]) => {
    const enrichedNullable = await Promise.all(
      rawChats.map(async (chat) => {
        if (chat.type === 'private' && chat.members?.length) {
          const peerId = chat.members.find((id) => id !== userId)
          if (peerId) {
            try {
              const peer = await usersApi.getProfile(peerId)
              return { ...chat, peer }
            } catch {
              return chat
            }
          }
          return { ...chat, title: 'Избранное' }
        }
        if (chat.type === 'group' && !chat.title) {
          try {
            const group = await groupsApi.get(chat.id)
            return { ...chat, title: group.name }
          } catch {
            return chat
          }
        }
        return chat
      })
    )
    const seenPeerIds = new Set<string>()
    return enrichedNullable
      .filter((c): c is NonNullable<typeof c> => c !== null)
      .filter((chat) => {
        if (chat.type !== 'private') return true
        const peerId = chat.peer?.id
        if (!peerId) return true
        if (seenPeerIds.has(peerId)) return false
        seenPeerIds.add(peerId)
        return true
      })
  }, [userId])

  const loadChats = useCallback(async (f: string) => {
    const cachedChats = await messageCache.getChats()
    if (cachedChats.length > 0) {
      setChats(cachedChats)
      setSyncing(true)
    } else {
      setLoading(true)
    }

    try {
      const syncTs = messageCache.getSyncTimestamp()
      const isDelta = !!syncTs && cachedChats.length > 0

      const res = await chatsApi.list(f, isDelta ? syncTs : undefined)
      const rawChats = (res.chats ?? []) as import('../../types').Chat[]

      if (isDelta) {
        if (rawChats.length > 0) {
          const enriched = await enrichChats(rawChats)
          // Merge updated chats into cached list
          const merged = [...cachedChats]
          for (const updated of enriched) {
            const idx = merged.findIndex((c) => c.id === updated.id)
            if (idx >= 0) merged[idx] = updated
            else merged.unshift(updated)
          }
          setChats(merged)
          await messageCache.saveChats(merged)
        }
        // else: nothing changed — cached chats are still current
      } else {
        if (rawChats.length === 0 && cachedChats.length > 0) {
          // Network returned empty but cache has data — keep cache
        } else {
          const enriched = await enrichChats(rawChats)
          setChats(enriched)
          await messageCache.saveChats(enriched)
        }
      }

      messageCache.setSyncTimestamp(new Date().toISOString())
    } catch {
      // Network error — cached data already shown
      if (cachedChats.length === 0) {
        addToast('error', 'Не удалось загрузить чаты')
      }
    } finally {
      setLoading(false)
      setSyncing(false)
    }
  }, [setChats, addToast, enrichChats])

  useEffect(() => {
    // Clear displayed chats immediately when folder changes so stale
    // inbox chats don't show while archive (or vice-versa) is loading.
    setChats([])
    loadChats(folder)
  }, [folder]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    channelsApi.listSubscriptions()
      .then((r) => setChannels(r.channels ?? []))
      .catch(() => {})
  }, [userId])

  const filtered = chats.filter((c) => {
    // Hide self-chat — enrichChats sets title='Избранное' but no peer field
    // (members contains only the current userId with no other participant)
    if (c.type === 'private' && !c.peer) return false

    // Guard: only show chats that belong to the active folder
    const chatFolder = (c as { folder?: string }).folder ?? 'inbox'
    if (folder === 'inbox' && chatFolder !== 'inbox') return false
    if (folder === 'archive' && chatFolder !== 'archive') return false
    if (folder.startsWith('custom_') && chatFolder !== folder) return false

    if (!search) return true
    const title = chatTitle(c).toLowerCase()
    return title.includes(search.toLowerCase())
  })

  if (modal === 'settings') {
    return <SettingsPanel onClose={() => setModal(null)} />
  }

  if (modal === 'contacts') {
    return <ContactsPanel onClose={() => setModal(null)} onOpenChat={() => setModal(null)} />
  }

  return (
    <>
      <div className="flex flex-col h-full bg-bg-primary">
        {/* Header — title + action icons */}
        <div className="flex items-center justify-between px-4 py-2.5 shrink-0">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setModal('settings')}
              className="p-1.5 rounded-xl text-txt-secondary hover:text-txt-primary hover:bg-bg-hover transition-colors"
              aria-label="Settings"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
            </button>
            <h1 className="text-txt-primary font-semibold text-[17px] leading-none">
              GGram
              {syncing && (
                <svg className="inline-block ml-1.5 animate-spin text-txt-meta align-middle" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                </svg>
              )}
            </h1>
          </div>

          <div className="flex items-center gap-0.5">
            <button
              onClick={() => setGlobalSearchOpen(true)}
              className="p-1.5 rounded-xl text-txt-secondary hover:text-txt-primary hover:bg-bg-hover transition-colors"
              aria-label="Search"
              title="Глобальный поиск (Ctrl+Shift+F)"
            >
              <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            </button>
            <button
              onClick={() => setModal('new-chat')}
              className="p-1.5 rounded-xl text-txt-secondary hover:text-txt-primary hover:bg-bg-hover transition-colors"
              aria-label="New chat"
            >
              <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Stories strip — right below header like Telegram */}
        <StoryFeed />

        {/* Search bar */}
        <div className="px-3 pb-2 pt-1 shrink-0">
          <div className="flex items-center bg-bg-input rounded-xl px-3 gap-2">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-txt-meta shrink-0">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text"
              placeholder="Поиск"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 bg-transparent text-txt-primary py-2 text-sm outline-none placeholder-txt-meta"
            />
            {search && (
              <button onClick={() => setSearch('')} className="text-txt-meta hover:text-txt-secondary text-xs">✕</button>
            )}
          </div>
        </div>

        {/* Folder tabs — Telegram-style pills */}
        <div className="flex gap-1 px-3 pb-1.5 overflow-x-auto scrollbar-none shrink-0">
          {folders.map((f) => (
            editingFolderId === f.id ? (
              <form
                key={f.id}
                className="flex items-center gap-0.5 shrink-0"
                onSubmit={(e) => {
                  e.preventDefault()
                  if (editFolderName.trim()) updateFolder(f.id, editFolderName.trim(), f.icon)
                  setEditingFolderId(null)
                }}
              >
                <input
                  autoFocus
                  value={editFolderName}
                  onChange={(e) => setEditFolderName(e.target.value)}
                  maxLength={24}
                  className="w-24 bg-bg-input text-txt-primary rounded-full px-2.5 py-1 text-xs outline-none focus:ring-1 focus:ring-accent"
                />
                <button type="submit" className="text-accent text-xs px-1 hover:opacity-80">✓</button>
                <button
                  type="button"
                  onClick={() => { removeFolder(f.id); if (folder === f.id) setFolder('inbox'); setEditingFolderId(null) }}
                  className="text-red-400 text-xs px-1 hover:opacity-80"
                  title="Удалить папку"
                >✕</button>
              </form>
            ) : (
              <button
                key={f.id}
                onClick={() => setFolder(f.id)}
                onDoubleClick={() => {
                  if (f.id !== 'inbox' && f.id !== 'archive') {
                    setEditingFolderId(f.id)
                    setEditFolderName(f.name)
                  }
                }}
                className={`shrink-0 px-3 py-1 text-[13px] font-medium rounded-full transition-colors whitespace-nowrap
                  ${folder === f.id
                    ? 'bg-accent text-white'
                    : 'bg-bg-input text-txt-secondary hover:text-txt-primary'
                  }`}
                title={f.id !== 'inbox' && f.id !== 'archive' ? 'Двойной клик для переименования' : undefined}
              >
                {f.name}
                {(folderUnread[f.id] ?? 0) > 0 && (
                  <span className={`ml-1.5 text-[11px] font-semibold ${folder === f.id ? 'text-white/80' : 'text-accent'}`}>
                    {(folderUnread[f.id] ?? 0) > 99 ? '99+' : folderUnread[f.id]}
                  </span>
                )}
              </button>
            )
          ))}
          <button
            onClick={() => setShowNewFolder((v) => !v)}
            className="shrink-0 px-3 py-1 text-[13px] text-txt-meta hover:text-txt-secondary bg-bg-input rounded-full transition-colors"
            title="Новая папка"
          >
            +
          </button>
        </div>
        {showNewFolder && (
          <form
            className="flex gap-1 px-3 pb-2"
            onSubmit={(e) => {
              e.preventDefault()
              if (newFolderName.trim()) {
                addFolder(newFolderName.trim(), '📂')
                setNewFolderName('')
                setShowNewFolder(false)
              }
            }}
          >
            <input
              autoFocus
              type="text"
              placeholder="Название папки..."
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              maxLength={24}
              className="flex-1 bg-bg-input text-txt-primary rounded-full px-3 py-1 text-xs outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta"
            />
            <button type="submit" className="text-accent text-xs px-2 font-medium hover:opacity-80">Создать</button>
            <button type="button" onClick={() => setShowNewFolder(false)} className="text-txt-meta text-xs px-1 hover:text-txt-secondary">✕</button>
          </form>
        )}

        {/* Saved Messages shortcut */}
        <SavedMessagesButton
          activeChatId={activeChatId}
          chats={chats}
          userId={userId}
          addToast={addToast}
        />

        {/* Channels section */}
        {channels.length > 0 && (
          <div className="border-b border-divider">
            <button
              onClick={() => setChannelsExpanded((v) => !v)}
              className="flex items-center justify-between w-full px-4 py-2 hover:bg-bg-hover transition-colors"
            >
              <div className="flex items-center gap-2">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-accent">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 14a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 3h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 10.91a16 16 0 0 0 5.99 5.99l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 19.73z"/>
                </svg>
                <span className="text-txt-secondary text-xs font-medium uppercase tracking-wide">Каналы</span>
                <span className="bg-bg-input text-txt-meta text-[10px] px-1.5 py-0.5 rounded-full">{channels.length}</span>
              </div>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`text-txt-meta transition-transform ${channelsExpanded ? 'rotate-180' : ''}`}>
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
            {channelsExpanded && (
              <div className="pb-1 px-2 space-y-0.5">
                {channels.map((ch) => (
                  <button
                    key={ch.id}
                    onClick={() => setActiveChannelId(ch.id)}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-bg-hover transition-colors text-left"
                  >
                    <div className="w-9 h-9 rounded-full bg-accent/15 flex items-center justify-center shrink-0 text-sm font-bold text-accent">
                      {ch.name[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-txt-primary text-sm font-medium truncate">{ch.name}</p>
                      <p className="text-txt-meta text-xs truncate">{ch.subscriber_count} подписчиков</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center pt-8">
              <Spinner />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center pt-10 gap-3 text-center px-4">
              <div className="w-12 h-12 rounded-full bg-bg-input flex items-center justify-center">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-txt-meta">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <p className="text-txt-secondary text-sm">
                {search ? 'Чаты не найдены' : folder === 'archive' ? 'Архив пуст' : 'Нет чатов'}
              </p>
              {!search && folder === 'inbox' && (
                <button
                  onClick={() => setModal('new-chat')}
                  className="text-accent text-sm hover:underline"
                >
                  Начать переписку
                </button>
              )}
            </div>
          ) : (
            filtered.map((chat) => (
              <ChatItem
                key={chat.id}
                chat={chat}
                isActive={chat.id === activeChatId}
                onClick={() => setActiveChat(chat.id)}
                onFolderChange={() => loadChats(folder)}
              />
            ))
          )}
        </div>
      </div>

      {modal === 'new-chat' && (
        <NewChatModal
          onClose={() => setModal(null)}
          onCreateGroup={() => setModal('create-group')}
          onCreateChannel={() => setModal('create-channel')}
          onBotStore={() => setModal('bot-store')}
        />
      )}

      {modal === 'bot-store' && (
        <BotStore onClose={() => setModal(null)} />
      )}

      {modal === 'create-group' && (
        <CreateGroupModal onClose={() => setModal(null)} />
      )}

      {modal === 'create-channel' && (
        <CreateChannelModal
          onClose={() => setModal(null)}
          onCreate={(ch) => {
            setChannels((prev) => [ch, ...prev])
            setChannelsExpanded(true)
            setActiveChannelId(ch.id)
          }}
        />
      )}

      {activeChannelId && (
        <ChannelView channelId={activeChannelId} onClose={() => setActiveChannelId(null)} />
      )}
    </>
  )
}
